import { getCookie } from 'util/cookie-util';
import hideWidget from 'util/hide-widget';
import isSdkMode from 'util/is-sdk-mode';

import { createContext, lazy, useState } from 'react';

import { ApolloProvider } from '@apollo/react-hooks';
import type { Session } from '@opentok/client';
import PopupChat from 'components/popup-chat/popup-chat';
import ProductExpert from 'components/product-expert/product-expert';
import { FORWARDING } from 'const/session-storage';
import { ChatWindowOpenStyle, POPUP_WINDOW } from 'context/app-window-style';
import BasketContextProvider from 'context/basket-context';
import { useCall } from 'context/call-context';
import { useStoreDetails } from 'context/store-context';
import customerLeaveSession from 'data/customer-leave-session';
import client from 'gql/client';
import { Action, Index } from 'hooks/models';
import useActivityLogger from 'hooks/useActivityLogger';

const SurveyModel = lazy(() => import('../survey-model/survey-model'));

interface IChatContext {
  productExpertView: boolean;
  popupChat: boolean;
  closePopupChat: () => void;
  openProductExpert: (data: {
    session: Session;
    agentName: string;
    storeName: string;
    mode: string;
    expertName: string;
  }) => void;
  closeProductExpert: () => Promise<void>;
  type: string;
  agentName?: string;
  expertName?: string;
  session: Session;
  showSurveyPrompt: boolean;
}

interface IChatWindow {
  type: string;
  baseUrl: string;
}

export const ChatContext = createContext<IChatContext>({} as IChatContext);
const { Provider } = ChatContext;

const ChatWindow = ({ type, baseUrl }: IChatWindow) => {
  const { chatWindowOpenStyle, directCallEnabled } = useCall();
  const { log } = useActivityLogger();
  const { customerSurvey, mergeToCart, name: storeName } = useStoreDetails();
  const [popupChat, setPopupChat] = useState<boolean>(false);
  const [productExpertView, setProductExpertView] = useState<boolean>(false);
  const [session, setSession] = useState<Session>();
  const [agentName, setAgentName] = useState<string>();
  const [expertName, setExpertName] = useState<string>('');
  const [mode, setmode] = useState<string>('');
  const [showSurveyPrompt, setShowSurveyPrompt] = useState<boolean>(false);
  const cookieValue = getCookie('platform');

  const closePopupChat = () => setPopupChat(false);

  const openProductExpert = (data: {
    session: Session;
    agentName: string;
    storeName: string;
    mode: string;
    expertName: string;
  }) => {
    setSession(data.session);
    setAgentName(data.agentName);
    setExpertName(data.expertName);
    setmode(data.mode);
    setProductExpertView(true);
    if (directCallEnabled) {
      void log({
        action: Action.CLICK_START_BUTTON,
        agentUsername: data.agentName,
        description: 'Customer started the call.',
        index: Index.ACTIVITY,
        label: 'CALL TYPE - DIRECTCALL',
        sessionID: data.session ? data.session.sessionId : 'NO_SESSION'
      });
    } else {
      void log({
        action: Action.CLICK_START_BUTTON,
        agentUsername: data.agentName,
        description: 'Customer started the call.',
        index: Index.ACTIVITY,
        label: 'CALL TYPE - WALKIN',
        sessionID: data.session ? data.session.sessionId : 'NO_SESSION'
      });
    }
  };

  const onCallEndReload = () => {
    if (sessionStorage.getItem('checkOuting') === 'true' && mergeToCart?.cartUrl) {
      window.location.href = mergeToCart.cartUrl;
      sessionStorage.removeItem('checkOuting');
    } else {
      const productPageUrl = sessionStorage.getItem('currentProductURL');
      window.location.href = productPageUrl || '';
    }
  };

  const closeProductExpert = async () => {
    const isForwarding = sessionStorage.getItem(FORWARDING);
    if (isForwarding === 'true') return;

    setProductExpertView(false);
    if (customerSurvey?.elements) {
      setShowSurveyPrompt(true);
    } else if (isSdkMode()) {
      hideWidget();
    }
    session?.disconnect();
    if (agentName) {
      await customerLeaveSession({
        agentUsername: agentName,
        isMissedCall: false,
        sessionId: session?.sessionId,
        storeBaseUrl: baseUrl
      });
    }
    if (!customerSurvey?.elements) {
      onCallEndReload();
    }
  };

  const onSubmitSurvey = (data: unknown) => {
    const surveyObj = {
      agentName: agentName,
      sessionID: session?.sessionId,
      storeName: storeName,
      surveyResponse: data
    };
    void log({
      action: Action.CUSTOMER_SUBMIT_SURVEY,
      agentUsername: agentName,
      description: 'customer submit the survey',
      index: Index.ACTIVITY,
      label: 'SURVEY',
      sessionID: session?.sessionId,
      survey: surveyObj
    });
    setTimeout(() => {
      setShowSurveyPrompt(false);
      if (customerSurvey?.elements) {
        onCallEndReload();
      }
    }, 2000);
  };

  const initProvider: IChatContext = {
    agentName,
    closePopupChat,
    closeProductExpert,
    expertName,
    openProductExpert,
    popupChat,
    productExpertView,
    session: session!,
    showSurveyPrompt,
    type
  };

  return (
    <Provider value={initProvider}>
      {(chatWindowOpenStyle === ChatWindowOpenStyle.MODEL || window.name === POPUP_WINDOW) && (
        <>
          {productExpertView && (
            // @ts-ignore
            <ApolloProvider client={client}>
              {agentName && session && (
                <BasketContextProvider
                  session={session}
                  agentName={agentName}
                >
                  <ProductExpert
                    session={session!}
                    agentName={agentName}
                    mode={mode}
                    expertName={expertName}
                  />
                </BasketContextProvider>
              )}
            </ApolloProvider>
          )}
          {showSurveyPrompt && customerSurvey && customerSurvey.elements?.length > 0 && (
            <SurveyModel
              onSubmitSurvey={(data: unknown) => onSubmitSurvey(data)}
              surveyJSON={customerSurvey}
            />
          )}
        </>
      )}
      {!cookieValue && cookieValue?.toLowerCase() !== 'mobileapp' && !productExpertView && (
        <PopupChat baseUrl={baseUrl} />
      )}
    </Provider>
  );
};

export default ChatWindow;
